import type { AgGridReact } from "ag-grid-react";
import type { RefObject } from "react";

/**
 * Creates a function to determine if a row is the last row on the current page.
 * Used to detect when Tab should escape the grid to pagination controls.
 */
export function createIsLastRowChecker<T extends { id: string }>(
  gridRef: RefObject<AgGridReact<T> | null>,
  sortedData: T[],
  pageSize: number,
): (id: string) => boolean {
  return (id: string): boolean => {
    const api = gridRef.current?.api;

    // If the grid API isn't available yet, fall back to the last row overall.
    if (!api) {
      return sortedData[sortedData.length - 1]?.id === id;
    }

    const currentPageSize = api.paginationGetPageSize?.() ?? pageSize;
    const currentPage = api.paginationGetCurrentPage?.() ?? 0;

    const startIndex = currentPage * currentPageSize;
    const endIndexExclusive = Math.min(startIndex + currentPageSize, sortedData.length);
    const lastRowOnPage = sortedData[endIndexExclusive - 1];

    return lastRowOnPage?.id === id;
  };
}

/**
 * Creates a keyboard event handler for the last interactive element in a grid row.
 * When Tab is pressed on the last row, moves focus to pagination controls instead of looping.
 * 
 * @param isLast - Whether this is the last row on the current page
 * @param wrapperClassName - CSS module class name for the grid wrapper container
 * @returns Keyboard event handler
 */
export function createLastRowTabHandler(
  isLast: boolean,
  wrapperClassName: string,
): (event: React.KeyboardEvent) => void {
  return (event: React.KeyboardEvent): void => {
    // Only handle Tab on the last row when tabbing forward
    if (!isLast || event.key !== "Tab" || event.shiftKey) {
      return;
    }

    const currentTarget = event.currentTarget;

    // Find the pagination controls and focus the first button
    const gridWrapper = currentTarget.closest('[role="grid"]') as HTMLElement | null;
    let nextFocusTarget: HTMLElement | null = null;

    if (gridWrapper) {
      const container =
        gridWrapper.closest(`.${wrapperClassName}`) ??
        gridWrapper.parentElement ??
        document.body;

      nextFocusTarget = container.querySelector(
        '[aria-label*="page"], [aria-label*="Page"], button[class*="pagination"]',
      ) as HTMLElement | null;

      if (!nextFocusTarget) {
        // Fallback: find the next focusable button scoped to this component
        const buttons = Array.from(container.querySelectorAll("button")).filter((btn) => {
          const isDisabled = (btn as HTMLButtonElement).disabled;
          const isHidden = btn.offsetParent === null || btn.getAttribute("aria-hidden") === "true";
          return !isDisabled && !isHidden;
        });

        const currentIndex = buttons.findIndex(
          (btn) => btn === currentTarget || btn.contains(currentTarget as Node),
        );

        if (currentIndex >= 0 && currentIndex < buttons.length - 1) {
          nextFocusTarget = buttons[currentIndex + 1] as HTMLElement;
        }
      }
    }

    // Only prevent default if we can successfully move focus; otherwise let the browser handle Tab.
    if (nextFocusTarget) {
      event.preventDefault();
      nextFocusTarget.focus();
    }
  };
}

/**
 * Keyboard event handler for AG-Grid columns with interactive elements.
 * Suppresses AG-Grid's Tab handling to allow browser to handle button navigation.
 */
export function suppressGridTabHandler(params: { event: KeyboardEvent }): boolean {
  // Suppress AG-Grid handling of Tab to let browser handle button navigation
  return params.event.key === "Tab";
}
